import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import type { GameHistoryRow, GameResult, GameType, ResultOutcome } from '@ottd/api';

import { ClimateThumb } from '@/components/prototype/climate-thumb';
import { FleetChips } from '@/components/prototype/fleet-chips';
import { GameStatusBadge } from '@/components/prototype/game-status-badge';
import { IdentityPair } from '@/components/prototype/identity-pair';
import { companyColorHex } from '@/components/prototype/president-face';
import { ProgressBar } from '@/components/prototype/progress-bar';
import { RelativeTime } from '@/components/prototype/relative-time';
import { TypeTile } from '@/components/prototype/type-tile';
import {
  formatMoney,
  formatPopulation,
  formatRelativeTime,
  formatSignedPoints,
} from '@/lib/format';
import { tr } from '@/lib/locale';
import { serverTypeTheme } from '@/lib/server-type';
import { cn } from '@/lib/utils';
import { bannerStyle } from '@/pages/shared';

/**
 * The API hands back raw numbers, so the wording lives here: a city-builder
 * company reports its town's population, a company-value one its valuation.
 * Null when there is no figure - a city-builder company that never claimed a
 * town has no result to show.
 */
function outcomeParts(
  outcome: ResultOutcome,
  type: GameType,
): { figure: string; detail: string } | null {
  if (outcome.figure === null) {
    return null;
  }

  if (type === 'CV') {
    return { figure: formatMoney(outcome.figure), detail: tr('game:history.companyValue') };
  }

  return {
    figure: tr('game:history.popFigure', { value: formatPopulation(outcome.figure) }),
    detail: outcome.detail ? tr('game:history.inTown', { town: outcome.detail }) : '',
  };
}

function ResultRow({ result, index, type }: { result: GameResult; index: number; type: GameType }) {
  const { t } = useTranslation('game');
  const { t: tc } = useTranslation('common');
  const isWinner = result.place === 'winner';
  const outcome = outcomeParts(result.outcome, type);
  const color = companyColorHex(result.color);
  return (
    <div className={cn('border-line flex border-b last:border-b-0', isWinner && 'bg-panel-in')}>
      {/* Company-colour spine, as on the game screen's card - with the rows
          stacked it turns the card's left edge into a colour key. Inset on
          every side so neighbouring rows read as separate chips instead of
          fusing into one stripe down the card. */}
      <span
        className="my-2.5 ml-1.5 w-1.5 shrink-0 border border-black"
        style={{ background: color }}
        aria-hidden
      />
      <div className="min-w-0 flex-1 py-2.5 pr-4 pl-2.5">
        <div className="flex flex-wrap items-center gap-3">
          {/* Rank and portrait ride together on a tighter gap - the row's 12px
              rhythm left the star stranded away from the face it ranks. */}
          <div className="flex shrink-0 items-center gap-1.5">
            <span
              className={cn(
                'font-display w-5 text-center text-[13px]',
                isWinner ? 'text-gold' : 'text-dim',
              )}
            >
              {isWinner ? '★' : index + 1}
            </span>
            <IdentityPair
              player={result.player}
              avatar={result.avatar}
              company={result.company}
              color={result.color}
            />
          </div>
          {/* Player first, company demoted to the byline: these games are won
            by people. */}
          <div className="min-w-0 flex-1">
            {result.player ? (
              <Link
                to={`/player/${encodeURIComponent(result.player)}`}
                className="font-display text-strong hover:text-gold block truncate text-[13px]"
              >
                {result.player}
              </Link>
            ) : (
              <span className="font-display text-dim block truncate text-[13px]">
                {t('history.guest')}
              </span>
            )}
            <p className="text-dim truncate text-xs">
              <span style={{ color }}>{result.company}</span>
              {outcome && (
                <>
                  <span className="text-faint"> · </span>
                  <span>
                    <span className="text-green">{outcome.figure}</span>
                    {outcome.detail}
                  </span>
                </>
              )}
            </p>
          </div>
          <FleetChips fleet={result.fleet} variant="inline" className="hidden sm:flex" />
          <span
            className={cn(
              'font-display w-16 shrink-0 text-right text-[11px]',
              isWinner ? 'text-gold' : 'text-green',
            )}
          >
            {/* points is a non-nullable number; 0 is a real score → "+0 PTS", not "-". */}
            {tc('units.points', { points: formatSignedPoints(result.points) })}
          </span>
        </div>
        {/* Spans the whole row, so the bars line up across companies instead
            of each one scaling to its own column. */}
        <ProgressBar
          value={result.progress}
          height={6}
          label={t('history.progressLabel', { company: result.company })}
          className="mt-1"
        />
      </div>
    </div>
  );
}

/**
 * The "when" line: played / cancelled at the moment the game finished, or -
 * on the legacy all-statuses list - started, for one still running.
 */
function whenLabel(game: GameHistoryRow): { iso: string; label: string } | null {
  if (game.status === 'live') {
    return game.startedAt
      ? {
          iso: game.startedAt,
          label: tr('game:history.startedAgo', { ago: formatRelativeTime(game.startedAt) }),
        }
      : null;
  }
  if (!game.playedAt) return null;
  const key = game.status === 'cancelled' ? 'game:history.cancelledAgo' : 'game:history.playedAgo';

  return { iso: game.playedAt, label: tr(key, { ago: formatRelativeTime(game.playedAt) }) };
}

export function GameHistoryCard({ game }: { game: GameHistoryRow }) {
  const { t } = useTranslation('game');
  const theme = serverTypeTheme(game.type);
  const when = whenLabel(game);
  return (
    <article className="bevel bg-panel shadow-hard-sm">
      <div className="flex flex-wrap items-center gap-3 px-4 py-2.5" style={bannerStyle(game.type)}>
        <TypeTile type={game.type} size={36} />
        <ClimateThumb climate={game.climate} width={52} />
        <div className="min-w-0">
          <p
            className="font-display text-[13px] text-white"
            style={{ textShadow: '2px 2px 0 #000' }}
          >
            {game.server}{' '}
            <Link to={`/game/${game.id}`} style={{ color: theme.linkColor }}>
              #{game.id}
            </Link>
          </p>
          <p className="font-mono text-[11px]" style={{ color: theme.subColor }}>
            {t('history.goal')} <span className="text-green font-bold">{game.goal}</span>
          </p>
        </div>
        <div className="ml-auto text-right">
          {/* Only the legacy `?all=1` list holds running / cancelled games. */}
          {game.status !== 'ended' ? <GameStatusBadge status={game.status} /> : null}
          {/* When it was played ("played 3 days ago"), the exact moment on
              hover - the legacy list carried both. */}
          {when ? (
            <p className="font-mono text-[11px]" style={{ color: theme.subColor }}>
              <time dateTime={when.iso}>
                <RelativeTime iso={when.iso} label={when.label} />
              </time>
            </p>
          ) : null}
          {/* No winning time on record: nothing, never the game's length. An
              impossible one (time anomaly) says it could not be computed. */}
          {game.timeAnomaly || game.duration ? (
            <p className="font-mono text-[11px]" style={{ color: theme.subColor }}>
              {game.timeAnomaly
                ? t('history.timeUnavailable')
                : t('history.wonIn', { duration: game.duration })}
            </p>
          ) : null}
        </div>
      </div>

      <div>
        {game.results.map((result, index) => (
          <ResultRow key={`${game.id}-${index}`} result={result} index={index} type={game.type} />
        ))}
      </div>
    </article>
  );
}
